import type { MeetingListWhen } from '@fos/shared/contracts/meetings';
import { MEETING_LIST_WHEN_LABELS } from '@fos/shared/labels';
import { CalendarDays, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { Input } from '../../../ui/Input';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { MobileFab, MobileFilters, SEGMENTED_TOUCH, useIsPhone } from '../../mobile';
import { MeetingRow } from '../components/MeetingRow';
import { NewMeetingDialog } from '../components/NewMeetingDialog';
import { MeetingsNoAccess } from './MeetingsNoAccess';

/**
 * Lista spotkań `/spotkania` (dostęp z ⌘K i profilu klienta; bez pozycji w menu — DESIGN §4). Widoku nie ma
 * w Figmie: wzorzec list (wiersze bez kart, linia pod spodem). Nadchodzące / Przeszłe, filtr klienta, szukanie.
 * Adres: `?klient=ID` (filtr), `?projekt=ID`, `?widok=przeszle`, `?nowe=1` (dialog „Nowe spotkanie”).
 */

const ALL = '__wszyscy__';
const WHEN_BY_PARAM: Record<string, MeetingListWhen> = { nadchodzace: 'upcoming', przeszle: 'past' };
const PARAM_BY_WHEN: Record<'upcoming' | 'past', string> = { upcoming: 'nadchodzace', past: 'przeszle' };

export function MeetingsPage() {
  const access = useAccess();
  if (!access.can('clients')) return <MeetingsNoAccess />;
  return <MeetingsList />;
}

function MeetingsList() {
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const when = (WHEN_BY_PARAM[params.get('widok') ?? ''] ?? 'upcoming') as 'upcoming' | 'past';
  const clientId = params.get('klient') ?? undefined;
  const projectId = params.get('projekt') ?? undefined;
  const canCreate = access.can('clients', 'edit');
  const phone = useIsPhone();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const list = trpc.meetings.list.useQuery({
    when,
    ...(clientId ? { clientId } : {}),
    ...(projectId ? { projectId } : {}),
    ...(debounced.length >= 2 ? { q: debounced } : {}),
    limit: 100,
  });
  const options = trpc.meetings.formOptions.useQuery({});
  const clientOptions: ComboboxOption[] = useMemo(
    () => [
      { value: ALL, label: 'Wszyscy klienci' },
      ...(options.data?.clients ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [options.data],
  );

  const creating = params.get('nowe') === '1' && canCreate;
  const setParam = (key: string, value: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  const closeDialog = (open: boolean) => {
    if (open) return;
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.delete('nowe');
        return next;
      },
      { replace: true },
    );
  };

  const newButton = canCreate ? (
    <Button variant="primary" icon={Plus} onClick={() => setParam('nowe', '1')}>
      Nowe spotkanie
    </Button>
  ) : undefined;

  const filtered = Boolean(clientId || projectId || debounced);
  const clientFilter = (
    <Combobox
      aria-label="Klient"
      value={clientId ?? ALL}
      onValueChange={(v) => setParam('klient', v === ALL ? null : v)}
      options={clientOptions}
      searchPlaceholder="Szukaj klienta…"
      size={phone ? 'm' : 's'}
    />
  );
  const search = (
    <Input
      size={phone ? 'm' : 's'}
      icon={Search}
      value={q}
      onChange={(e) => setQ(e.target.value)}
      placeholder="Szukaj spotkania…"
      aria-label="Szukaj spotkania"
    />
  );
  return (
    <Page>
      <PageHeader
        title="Spotkania"
        meta="Notatki i transkrypcje spotkań. AI przygotuje podsumowanie, ustalenia i propozycje zadań."
        actions={phone ? undefined : newButton}
      />
      {/* telefon (lifting 17.10.2026): klient i wyszukiwanie w panelu „Filtry”, „Nowe spotkanie” pod kciukiem */}
      <div className="mt-6 flex items-center gap-3 md:flex-row">
        <Segmented
          aria-label="Które spotkania"
          className={SEGMENTED_TOUCH}
          value={when}
          onValueChange={(v) => setParam('widok', PARAM_BY_WHEN[v])}
          options={(['upcoming', 'past'] as const).map((v) => ({ value: v, label: MEETING_LIST_WHEN_LABELS[v] }))}
        />
        <span className="flex-1" />
        {phone ? (
          <MobileFilters
            count={(clientId ? 1 : 0) + (debounced ? 1 : 0)}
            onClear={() => {
              setQ('');
              setParam('klient', null);
            }}
            showLabel="Pokaż spotkania"
          >
            {clientFilter}
            {search}
          </MobileFilters>
        ) : (
          <div className="grid w-[440px] grid-cols-2 gap-2">
            {clientFilter}
            {search}
          </div>
        )}
      </div>
      <Section className="mt-4">
        {list.isPending ? (
          <ListSkeleton rows={5} />
        ) : list.isError ? (
          <ErrorState onRetry={() => void list.refetch()} retrying={list.isFetching} />
        ) : list.data.items.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title={
              filtered
                ? 'Nic nie pasuje do filtrów'
                : when === 'upcoming'
                  ? 'Brak zaplanowanych spotkań'
                  : 'Nie ma jeszcze spotkań'
            }
            description={
              filtered
                ? 'Zmień klienta albo wyszukiwaną frazę.'
                : 'Dodaj spotkanie z notatkami albo transkrypcją, a AI przygotuje podsumowanie i zadania.'
            }
            action={filtered ? undefined : newButton}
          />
        ) : (
          <>
            <ul aria-label="Spotkania">
              {list.data.items.map((m) => (
                <li key={m.id}>
                  <MeetingRow meeting={m} hideClient={Boolean(clientId)} />
                </li>
              ))}
            </ul>
            {list.data.truncated && (
              <p className="pt-3 text-caption text-fg-muted">Pokazano 100 spotkań. Zawęź listę filtrem klienta.</p>
            )}
          </>
        )}
      </Section>
      {canCreate && phone && (
        <MobileFab icon={Plus} onClick={() => setParam('nowe', '1')}>
          Nowe spotkanie
        </MobileFab>
      )}
      {canCreate && (
        <NewMeetingDialog
          open={creating}
          onOpenChange={closeDialog}
          defaultClientId={clientId ?? null}
          defaultProjectId={projectId ?? null}
        />
      )}
    </Page>
  );
}
